import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Calendar, Clock, Bot, Play, Pause, Settings, Plus, Edit, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAIAgents } from '@/hooks/useAIAgents';
import { format, addMinutes, addHours, addDays } from 'date-fns';

interface AgentSchedule {
  id: string;
  name: string;
  agent_id: string;
  schedule_type: 'interval' | 'cron' | 'once';
  schedule_config: {
    interval_minutes?: number;
    cron_expression?: string;
    execute_at?: string;
  };
  enabled: boolean;
  created_at: string;
  last_execution?: string;
  next_execution?: string;
  execution_count: number;
  input_data?: any;
}

export const AgentScheduler = () => {
  const { toast } = useToast();
  const { agents, executeAgent, isExecuting } = useAIAgents();
  const [schedules, setSchedules] = useState<AgentSchedule[]>([]);
  const [isCreatingSchedule, setIsCreatingSchedule] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [scheduleType, setScheduleType] = useState<string>('interval');

  // Sample schedules
  useEffect(() => {
    const sampleSchedules: AgentSchedule[] = [
      {
        id: '1',
        name: 'Daily Data Validation',
        agent_id: 'agent_1',
        schedule_type: 'interval',
        schedule_config: {
          interval_minutes: 1440 // 24 hours
        },
        enabled: true,
        created_at: '2024-01-15T10:00:00Z',
        last_execution: '2024-01-25T10:00:00Z',
        next_execution: format(addDays(new Date(), 1), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        execution_count: 10,
        input_data: {
          validation_type: 'daily_summary',
          include_analytics: true
        }
      },
      {
        id: '2',
        name: 'Hourly Anomaly Detection',
        agent_id: 'agent_2',
        schedule_type: 'interval',
        schedule_config: {
          interval_minutes: 60
        },
        enabled: true,
        created_at: '2024-01-12T14:30:00Z',
        last_execution: '2024-01-25T15:00:00Z',
        next_execution: format(addHours(new Date(), 1), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        execution_count: 240,
        input_data: {
          detection_sensitivity: 'high',
          check_recent_bills: true
        }
      },
      {
        id: '3',
        name: 'Weekly Performance Report',
        agent_id: 'agent_3',
        schedule_type: 'cron',
        schedule_config: {
          cron_expression: '0 9 * * 1' // Every Monday at 9 AM
        },
        enabled: false,
        created_at: '2024-01-10T09:15:00Z',
        last_execution: '2024-01-22T09:00:00Z',
        next_execution: '2024-01-29T09:00:00Z',
        execution_count: 3,
        input_data: {
          report_type: 'performance_summary',
          period_days: 7
        }
      },
      {
        id: '4',
        name: 'End of Month Processing',
        agent_id: 'agent_4',
        schedule_type: 'once',
        schedule_config: {
          execute_at: '2024-01-31T23:59:00Z'
        },
        enabled: true,
        created_at: '2024-01-20T16:00:00Z',
        next_execution: '2024-01-31T23:59:00Z',
        execution_count: 0,
        input_data: {
          processing_type: 'month_end_summary',
          generate_reports: true
        }
      }
    ];
    setSchedules(sampleSchedules);
  }, []);

  const toggleSchedule = (scheduleId: string, enabled: boolean) => {
    setSchedules(prev => prev.map(schedule => 
      schedule.id === scheduleId ? { ...schedule, enabled } : schedule
    ));

    toast({
      title: enabled ? "Schedule enabled" : "Schedule disabled",
      description: `Agent schedule has been ${enabled ? 'enabled' : 'disabled'}.`,
    });
  };

  const executeNow = async (scheduleId: string) => {
    const schedule = schedules.find(s => s.id === scheduleId);
    if (!schedule) return;

    executeAgent({
      agentId: schedule.agent_id,
      inputData: {
        ...schedule.input_data,
        executed_from: 'manual_schedule',
        schedule_id: scheduleId
      }
    });

    // Update execution count and last execution
    setSchedules(prev => prev.map(s => 
      s.id === scheduleId 
        ? { 
            ...s, 
            execution_count: s.execution_count + 1, 
            last_execution: new Date().toISOString() 
          }
        : s
    ));

    toast({
      title: "Agent execution started",
      description: `Executing "${schedule.name}" immediately.`,
    });
  };

  const deleteSchedule = (scheduleId: string) => {
    setSchedules(prev => prev.filter(s => s.id !== scheduleId));
    toast({
      title: "Schedule deleted",
      description: "Agent schedule has been deleted.",
    });
  };

  const getScheduleDescription = (schedule: AgentSchedule) => {
    switch (schedule.schedule_type) {
      case 'interval':
        const minutes = schedule.schedule_config.interval_minutes || 0;
        if (minutes >= 1440) {
          return `Every ${minutes / 1440} day(s)`;
        } else if (minutes >= 60) {
          return `Every ${minutes / 60} hour(s)`;
        } else {
          return `Every ${minutes} minute(s)`;
        }
      case 'cron':
        return `Cron: ${schedule.schedule_config.cron_expression}`;
      case 'once':
        return `Once at ${format(new Date(schedule.schedule_config.execute_at || ''), 'MMM dd, yyyy HH:mm')}`;
      default:
        return 'Unknown schedule';
    }
  };

  const getStatusBadge = (schedule: AgentSchedule) => {
    if (!schedule.enabled) {
      return <Badge variant="outline">Disabled</Badge>;
    }
    
    if (schedule.schedule_type === 'once') {
      const executeAt = new Date(schedule.schedule_config.execute_at || '');
      const now = new Date();
      if (executeAt > now) {
        return <Badge variant="default">Scheduled</Badge>;
      } else {
        return <Badge variant="secondary">Completed</Badge>;
      }
    }
    
    return <Badge variant="default">Active</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold">Agent Scheduler</h3>
          <p className="text-sm text-muted-foreground">
            Schedule AI agents to run automatically at specified times
          </p>
        </div>
        <Button onClick={() => setIsCreatingSchedule(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Schedule
        </Button>
      </div>

      {/* Schedule Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Schedules</CardTitle>
            <Calendar className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {schedules.filter(s => s.enabled).length}
            </div>
            <p className="text-xs text-muted-foreground">
              {schedules.length} total schedules
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Executions</CardTitle>
            <Bot className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {schedules.reduce((sum, s) => sum + s.execution_count, 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              All time executions
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Next Execution</CardTitle>
            <Clock className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold text-purple-600">
              {schedules
                .filter(s => s.enabled && s.next_execution)
                .sort((a, b) => new Date(a.next_execution!).getTime() - new Date(b.next_execution!).getTime())
                .slice(0, 1)
                .map(s => format(new Date(s.next_execution!), 'HH:mm'))[0] || '--:--'}
            </div>
            <p className="text-xs text-muted-foreground">
              Upcoming execution
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Recurring Tasks</CardTitle>
            <Clock className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {schedules.filter(s => s.schedule_type !== 'once').length}
            </div>
            <p className="text-xs text-muted-foreground">
              Repeating schedules
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Schedules Table */}
      <Card>
        <CardHeader>
          <CardTitle>Scheduled Agent Executions</CardTitle>
          <CardDescription>
            Manage automatic execution schedules for your AI agents
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Schedule Name</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead>Schedule</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Run</TableHead>
                <TableHead>Next Run</TableHead>
                <TableHead>Executions</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schedules.map((schedule) => {
                const agent = agents?.find(a => a.id === schedule.agent_id);
                return (
                  <TableRow key={schedule.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{schedule.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {getScheduleDescription(schedule)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {agent ? (
                        <div className="flex items-center space-x-2">
                          <Bot className="h-4 w-4" />
                          <span className="text-sm">{agent.name}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">No agent</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {schedule.schedule_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={schedule.enabled}
                          onCheckedChange={(checked) => toggleSchedule(schedule.id, checked)}
                        />
                        {getStatusBadge(schedule)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {schedule.last_execution ? (
                        <span className="text-sm">
                          {format(new Date(schedule.last_execution), 'MMM dd, HH:mm')}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">Never</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {schedule.next_execution ? (
                        <span className="text-sm">
                          {format(new Date(schedule.next_execution), 'MMM dd, HH:mm')}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{schedule.execution_count}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-1">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => executeNow(schedule.id)}
                          disabled={isExecuting || !schedule.enabled}
                        >
                          <Play className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => deleteSchedule(schedule.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Schedule Modal */}
      {isCreatingSchedule && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <CardTitle>Create Agent Schedule</CardTitle>
              <CardDescription>
                Set up automatic execution schedule for an AI agent
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Schedule Name</label>
                  <Input placeholder="Enter schedule name..." />
                </div>
                <div>
                  <label className="text-sm font-medium">AI Agent</label>
                  <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select an agent..." />
                    </SelectTrigger>
                    <SelectContent>
                      {agents?.map((agent) => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Schedule Type</label>
                <Select value={scheduleType} onValueChange={setScheduleType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="interval">Recurring Interval</SelectItem>
                    <SelectItem value="cron">Cron Expression</SelectItem>
                    <SelectItem value="once">One-time Execution</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {scheduleType === 'interval' && (
                <div>
                  <label className="text-sm font-medium">Interval (minutes)</label>
                  <Input type="number" placeholder="60" />
                </div>
              )}

              {scheduleType === 'cron' && (
                <div>
                  <label className="text-sm font-medium">Cron Expression</label>
                  <Input placeholder="0 */6 * * *" />
                </div>
              )}

              {scheduleType === 'once' && (
                <div>
                  <label className="text-sm font-medium">Execute At</label>
                  <Input type="datetime-local" />
                </div>
              )}

              <Separator />

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsCreatingSchedule(false)}>
                  Cancel
                </Button>
                <Button onClick={() => setIsCreatingSchedule(false)}>
                  Create Schedule
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};